import React, { Suspense, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import {
  HMSRoomProvider,
  selectIsConnectedToRoom,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import {
  Box,
  Flex,
  globalStyles,
  HMSThemeProvider,
} from "@100mslive/roomkit-react";
import { AppData } from "./components/AppData/AppData.jsx";
import { BeamSpeakerLabelsLogging } from "./components/AudioLevel/BeamSpeakerLabelsLogging";
import ErrorPage from "./components/ErrorPage";
import FullPageProgress from "./components/FullPageProgress";
import { Init } from "./components/init/Init";
import { KeyboardHandler } from "./components/Input/KeyboardInputManager";
import { Notifications } from "./components/Notifications";
import { ToastContainer } from "./components/Toast/ToastContainer";
import { ErrorBoundary } from "./core/components/ErrorBoundary";
import PostLeave from "./core/components/PostLeave";
import {
  LiveHodoContext,
  LiveHodoContextProvider,
} from "./contexts/LiveHodoContext.js";
import i18n from "./lang/index.js";
import { palette } from "./theme.js";
import { Confetti } from "./plugins/confetti";
import { FlyingEmoji } from "./plugins/FlyingEmoji.jsx";
import { RemoteStopScreenshare } from "./plugins/RemoteStopScreenshare";
import { getRoutePrefix, shadeColor } from "./common/utils";
import { FeatureFlags } from "./services/FeatureFlags";

import { resetAll } from "@/reducers/counter";

const Conference = React.lazy(() => import("./core/components/Conference"));
const TelemedicineConference = React.lazy(
  () => import("./core/components/TelemedicineConference")
);
const MdtConference = React.lazy(
  () => import("./core/components/MdtConference")
);
const CabinConference = React.lazy(
  () => import("./core/components/CabinConference")
);
const SelectCabin = React.lazy(() => import("./core/components/SelectCabin"));
const PreviewScreen = React.lazy(
  () => import("./core/components/PreviewScreen")
);
const HomePage = React.lazy(() => import("./core/components/Home"));
const SignIn = React.lazy(() => import("./core/components/SignIn"));

const defaultTokenEndpoint = process.env.REACT_APP_TOKEN_GENERATION_ENDPOINT;
const envPolicyConfig = JSON.parse(process.env.REACT_APP_POLICY_CONFIG || "{}");

let appName;
if (window.location.host.includes("localhost")) {
  appName = "localhost";
} else {
  appName = window.location.host.split(".")[0];
}

document.title =
  process.env.REACT_APP_TITLE || `${appName}'s ${document.title}`;

// TODO: remove now that there are options to change to portrait
const getAspectRatio = ({ width, height }) => {
  const host = process.env.REACT_APP_HOST_NAME || window.location.hostname;
  const portraitDomains = (
    process.env.REACT_APP_PORTRAIT_MODE_DOMAINS || ""
  ).split(",");
  if (portraitDomains.includes(host) && width > height) {
    return { width: height, height: width };
  }
  return { width, height };
};

export function EdtechComponent({
  tokenEndpoint = defaultTokenEndpoint,
  themeConfig: {
    aspectRatio = "1-1",
    font = "Roboto",
    color = "#2F80FF",
    theme = "dark",
    logo = "",
    headerPresent = "false",
    metadata = "",
  },
  policyConfig = envPolicyConfig,
  getDetails = () => {},
  authTokenByRoomCodeEndpoint = "",
}) {
  const { 0: width, 1: height } = aspectRatio
    .split("-")
    .map(el => parseInt(el));
  globalStyles();

  return (
    <ErrorBoundary>
      <HMSThemeProvider
        themeType={theme}
        aspectRatio={getAspectRatio({ width, height })}
        theme={{
          colors: {
            ...palette[theme],
            primary_default: color,
            primary_dim: shadeColor(color, -30),
            primary_bright: shadeColor(color, 30),
            primary_disabled: shadeColor(color, 10),
          },
          fonts: {
            sans: [font, "Inter", "sans-serif"],
          },
        }}
      >
        <HMSRoomProvider isHMSStatsOn={FeatureFlags.enableStatsForNerds}>
          <LiveHodoContextProvider>
            <AppData
              appDetails={metadata}
              policyConfig={policyConfig}
              logo={logo}
              tokenEndpoint={tokenEndpoint}
            />

            <Init />
            <Box
              css={{
                bg: "$background_dim",
                w: "100%",
                lineHeight: "1.5",
                "-webkit-text-size-adjust": "100%",
                ...(headerPresent === "true"
                  ? { flex: "1 1 0", minHeight: 0 }
                  : { h: "100%" }),
              }}
            >
              <AppRoutes
                getDetails={getDetails}
                authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint}
              />
            </Box>
          </LiveHodoContextProvider>
        </HMSRoomProvider>
      </HMSThemeProvider>
    </ErrorBoundary>
  );
}

const RedirectToPreview = ({ getDetails }) => {
  const { roomId, role } = useParams();
  useEffect(() => {
    getDetails();
  }, [roomId]); //eslint-disable-line

  // console.error({ roomId, role });

  if (!roomId && !role) {
    return <Navigate to="/" />;
  }
  if (!roomId) {
    return <Navigate to="/" />;
  }
  // if (["streaming", "preview", "meeting", "leave"].includes(roomId) && !role) {
  //   return <Navigate to="/" />;
  // }
  if (
    ["streaming", "preview", "meeting", "cabin", "select", "leave"].includes(
      roomId
    ) &&
    !role
  ) {
    return <Navigate to="/" />;
  }

  return (
    <Navigate to={`${getRoutePrefix()}/preview/${roomId}/${role || ""}`} />
  );
};

const SetupBackdrop = ({ children }) => {
  const {
    stateCtx: { circleRoomDetail },
  } = useContext(LiveHodoContext);
  const [conferenceData, setConferenceData] = useState({});

  useEffect(() => {
    if (circleRoomDetail?.conference_circle?.id) {
      const conference = circleRoomDetail?.conference_circle?.conference;
      setConferenceData(conference);
    }
  }, [circleRoomDetail]);

  return (
    <Flex
      direction="column"
      css={{
        size: "100%",
        position: "relative",
        // backgroundImage: `url(https://images.unsplash.com/photo-1596522354195-e84ae3c98731)`,
        backgroundColor: "$surface_brighter",
        backgroundImage: conferenceData?.banner
          ? `url(${conferenceData?.banner})`
          : "",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box
        css={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
          zIndex: 0,
          backgroundColor: "$overlay",
        }}
      />
      <Flex
        direction="column"
        css={{ flex: "1 1 0", size: "100%", position: "relative", zIndex: 1 }}
      >
        {children}
      </Flex>
    </Flex>
  );
};

const RouteList = ({ getDetails, authTokenByRoomCodeEndpoint }) => {
  return (
    <Routes>
      <Route path="/" element={<HomeWithClear />} />
      <Route
        path="sign-in"
        element={
          <Suspense fallback={<FullPageProgress />}>
            <SignIn />
          </Suspense>
        }
      />
      <Route path="preview">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <PreviewScreen
                authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint}
              />
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <PreviewScreen
                authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint}
              />
            </Suspense>
          }
        />
      </Route>
      <Route path="select">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <SetupBackdrop>
                <SelectCabin />
              </SetupBackdrop>
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <SetupBackdrop>
                <SelectCabin />
              </SetupBackdrop>
            </Suspense>
          }
        />
      </Route>
      <Route path="cabin">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <SetupBackdrop>
                <CabinConference />
              </SetupBackdrop>
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <SetupBackdrop>
                <CabinConference />
              </SetupBackdrop>
            </Suspense>
          }
        />
      </Route>
      <Route path="meeting">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <Conference />
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <Conference />
            </Suspense>
          }
        />
      </Route>
      <Route path="telemedicine">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <TelemedicineConference />
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <TelemedicineConference />
            </Suspense>
          }
        />
      </Route>
      <Route path="mdt">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <MdtConference />
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <MdtConference />
            </Suspense>
          }
        />
      </Route>
      <Route path="pmdt">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <MdtConference />
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <MdtConference />
            </Suspense>
          }
        />
      </Route>
      <Route path="conference">
        <Route
          path=":roomId/:role"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <Conference />
            </Suspense>
          }
        />
        <Route
          path=":roomId"
          element={
            <Suspense fallback={<FullPageProgress />}>
              <Conference />
            </Suspense>
          }
        />
      </Route>
      <Route path="leave">
        <Route path=":roomId/:role" element={<PostLeave />} />
        <Route path=":roomId" element={<PostLeave />} />
      </Route>
      <Route
        path="/:roomId/:role"
        element={<RedirectToPreview getDetails={getDetails} />}
      />
      <Route
        path="/:roomId/"
        element={<RedirectToPreview getDetails={getDetails} />}
      />
      <Route
        path="/*"
        element={<ErrorPage error={i18n.t("lbl_invalid_url")} />}
      />
    </Routes>
  );
};

const HomeWithClear = () => {
  const dispatch = useDispatch();
  const { stateCtx } = useContext(LiveHodoContext);
  useEffect(() => {
    localStorage.removeItem("meet_room_id");
    localStorage.removeItem("room_id");
    localStorage.removeItem("type");
    stateCtx.circleRoomDetail = null;
    stateCtx.twlMeetRoom = null;
    dispatch(resetAll());
  }, [dispatch, stateCtx]);

  return (
    <Suspense fallback={<FullPageProgress />}>
      <HomePage />
    </Suspense>
  );
};

const BackSwipe = () => {
  const isConnectedToRoom = useHMSStore(selectIsConnectedToRoom);
  const hmsActions = useHMSActions();
  useEffect(() => {
    const onRouteLeave = async () => {
      if (isConnectedToRoom) {
        await hmsActions.leave();
      }
    };
    window.addEventListener("popstate", onRouteLeave);
    return () => {
      window.removeEventListener("popstate", onRouteLeave);
    };
  }, [hmsActions, isConnectedToRoom]);
  return null;
};

function AppRoutes({ getDetails, authTokenByRoomCodeEndpoint }) {
  return (
    <Router>
      <ToastContainer />
      <Notifications />
      <BackSwipe />
      <Confetti />
      <FlyingEmoji />
      <RemoteStopScreenshare />
      <KeyboardHandler />
      <BeamSpeakerLabelsLogging />
      <Routes>
        <Route
          path="/*"
          element={
            <RouteList
              getDetails={getDetails}
              authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint}
            />
          }
        />
        <Route
          path="/streaming/*"
          element={
            <RouteList
              getDetails={getDetails}
              authTokenByRoomCodeEndpoint={authTokenByRoomCodeEndpoint}
            />
          }
        />
      </Routes>
    </Router>
  );
}

export default function App() {
  useEffect(() => {
    if (window.localStorage.getItem("lang")) {
      i18n.changeLanguage(window.localStorage.getItem("lang"));
    }
  }, []);

  return (
    <EdtechComponent
      themeConfig={{
        aspectRatio: process.env.REACT_APP_TILE_SHAPE,
        theme: process.env.REACT_APP_THEME,
        color: process.env.REACT_APP_COLOR,
        logo: process.env.REACT_APP_LOGO,
        font: process.env.REACT_APP_FONT,
        headerPresent: process.env.REACT_APP_HEADER_PRESENT,
        metadata: process.env.REACT_APP_DEFAULT_APP_DETAILS, // A stringified object in env
      }}
    />
  );
}
